import { axiosInstance, baseUrl } from "./auth";
import { useQuery } from 'react-query'
import { ApiStatus } from "../constants";

// AI Usage & Cost - all provider calls go through the backend; API keys never reach the browser.

const unwrap = (response) => {
    if (response.data.code !== ApiStatus.success) {
        throw new Error(response.data.message || ApiStatus.serverError)
    }
    return response.data
}

/** Drops empty filters so the query string only has what was chosen */
const clean = (params = {}) =>
    Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))

const get = async (url, params) => unwrap(await axiosInstance.get(`${baseUrl}${url}`, { params: clean(params) }))

// ai-cost dashboard (reads MongoDB)
export const getAICostSummary = (params) => get('/ai-cost/summary', params)
export const getAICostDaily = (params) => get('/ai-cost/daily', params)
export const getAICostProviders = (params) => get('/ai-cost/providers', params)
export const getAICostModels = (params) => get('/ai-cost/models', params)
export const getAICostFeatures = (params) => get('/ai-cost/features', params)
export const getAICostLogs = (params) => get('/ai-cost/logs', params)
export const getAICostFilters = (params) => get('/ai-cost/filters', params)
export const getDeepgramRequestDetails = (requestId, projectId) =>
    get(`/ai-cost/deepgram/requests/${encodeURIComponent(requestId)}`, { projectId })

export const getGeminiUsage = (params) => get('/ai-cost/gemini/usage', params)

export const syncDeepgram = async (body) => unwrap(await axiosInstance.post(`${baseUrl}/ai-cost/deepgram/sync`, body))

const queryOptions = { refetchOnWindowFocus: false, retry: false, keepPreviousData: true }

export const useAICostSummary = (params) => useQuery(['AI_COST_SUMMARY', params], () => getAICostSummary(params), queryOptions)
export const useAICostDaily = (params) => useQuery(['AI_COST_DAILY', params], () => getAICostDaily(params), queryOptions)
export const useAICostProviders = (params) => useQuery(['AI_COST_PROVIDERS', params], () => getAICostProviders(params), queryOptions)
export const useAICostModels = (params) => useQuery(['AI_COST_MODELS', params], () => getAICostModels(params), queryOptions)
export const useAICostFeatures = (params) => useQuery(['AI_COST_FEATURES', params], () => getAICostFeatures(params), queryOptions)
export const useAICostLogs = (params) => useQuery(['AI_COST_LOGS', params], () => getAICostLogs(params), queryOptions)
export const useAICostFilters = (params) =>
    useQuery(['AI_COST_FILTERS', params], () => getAICostFilters(params), { ...queryOptions, staleTime: 60000 })

export const useGeminiUsage = (params, enabled = true) =>
    useQuery(['AI_COST_GEMINI_USAGE', params], () => getGeminiUsage(params), { ...queryOptions, enabled })

export const AI_COST_QUERY_KEYS = ['AI_COST_SUMMARY', 'AI_COST_DAILY', 'AI_COST_PROVIDERS', 'AI_COST_MODELS', 'AI_COST_FEATURES', 'AI_COST_LOGS', 'AI_COST_FILTERS', 'AI_COST_GEMINI_USAGE']

// Projects > AI Integration (the Deepgram key is write-only; the API returns it masked)
export const getAIProjectSettings = async (projectId) =>
    unwrap(await axiosInstance.get(`${baseUrl}/ai-cost/projects/${projectId}/settings`))

export const saveAIProjectSettings = async (projectId, body) =>
    unwrap(await axiosInstance.put(`${baseUrl}/ai-cost/projects/${projectId}/settings`, body))

export const testAIProjectConnection = async (projectId, body) =>
    unwrap(await axiosInstance.post(`${baseUrl}/ai-cost/projects/${projectId}/test`, body))

/** Pulls the project's Gemini usage from Theratap now (the backend also does it every minute) */
export const syncGeminiProject = async (projectId) =>
    unwrap(await axiosInstance.post(`${baseUrl}/ai-cost/projects/${projectId}/gemini/sync`, {}))

// Settings > Gemini Pricing: model price list used to cost Gemini calls
export const getGeminiPricing = () => get('/ai-cost/pricing/gemini')

export const saveGeminiPricing = async (body) =>
    unwrap(await axiosInstance.put(`${baseUrl}/ai-cost/pricing/gemini`, body))
